import React from 'react';
import { motion } from 'framer-motion';
import { XCircle, ArrowLeft, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PaymentCancel: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-pink-100 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full bg-white rounded-3xl shadow-2xl p-12 text-center"
      >
        {/* Cancel Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="mb-8 flex justify-center"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-500 rounded-full blur-xl opacity-30"></div>
            <div className="relative bg-gradient-to-r from-gray-400 to-gray-500 rounded-full p-6">
              <XCircle className="w-24 h-24 text-white" />
            </div>
          </div>
        </motion.div>

        {/* Cancel Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Payment Cancelled
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            No worries! Your current plan is still active.
          </p>
        </motion.div>

        {/* Info Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-blue-50 rounded-2xl p-6 mb-8 border-2 border-blue-200"
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Heart className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-semibold text-gray-800">Still exploring?</h2>
          </div>
          <p className="text-sm text-gray-600">
            You're still on the <span className="font-semibold">Free Plan</span> and can continue using Cevee.
            <br />
            Upgrade anytime when you're ready for more features!
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-4"
        >
          <button
            onClick={() => navigate('/pricing')}
            className="w-full bg-gradient-to-r from-pink-500 via-orange-400 to-yellow-400 hover:from-pink-600 hover:via-orange-500 hover:to-yellow-500 text-white font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 flex items-center justify-center gap-2"
          >
            View Plans Again
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-4 px-6 rounded-xl border-2 border-gray-300 transition-all duration-200 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
        </motion.div>

        {/* Support Link */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 pt-6 border-t border-gray-200"
        >
          <p className="text-xs text-gray-500">
            Need help deciding? Check out our{' '}
            <a href="/pricing" className="text-pink-500 hover:underline font-semibold">
              feature comparison
            </a>{' '}
            or{' '}
            <a href="/support" className="text-pink-500 hover:underline font-semibold">
              contact support
            </a>
            .
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PaymentCancel;

